function subscribeToTeamStoreMetaobject(callback) {
  const handle =
    window.teamStoreUtilities?.buildTeamStoreHandle?.(window.teamProductData || {}) ||
    null;

  if (window.TeamStoreEvents?.subscribeToMetaobject && handle) {
    return window.TeamStoreEvents.subscribeToMetaobject(handle, callback);
  }

  const listener = (event) => {
    const detail = event?.detail;
    const metaobject = detail?.metaobject;

    if (!metaobject) return;
    if (handle && metaobject.handle && metaobject.handle !== handle) return;

    callback(metaobject, detail?.teamStoreCollection || null);
  };

  window.addEventListener('metaobjectDataRetrieved', listener);

  return () => {
    window.removeEventListener('metaobjectDataRetrieved', listener);
  };
}

try {
  class TeamStorePriceChart {
    constructor() {
      this.teamProductData = window.teamProductData || {};
      this.metaobject = this.teamProductData?.metaobject;
      this.viewModel = this.getViewModel(
        document.querySelector('.product-container')
      );

      /** @type {TeamStorePriceCalculator|null} */
      this.calculator = null;

      this.init();
    }

    hasPriceCalculator() {
      return Boolean(window.teamStoreUtilities?.PriceCalculator);
    }

    init() {
      if (!this.metaobject) {
        subscribeToTeamStoreMetaobject((metaobject, teamStoreCollection) => {
          this.teamProductData = window.teamProductData || this.teamProductData;
          this.metaobject = metaobject;
          this.teamProductData.metaobject = metaobject;
          if (teamStoreCollection) {
            this.teamProductData.teamStoreCollection = teamStoreCollection;
          }

          if (!this.hasPriceCalculator()) return;
          this._setupCalculator();
          this.bindEvents();
        });
      } else {
        if (!this.hasPriceCalculator()) return;
        this._setupCalculator();
        this.bindEvents();
      }
    }

    /** Create reusable price calculator */
    _setupCalculator() {
      if (!this.hasPriceCalculator()) return;

      this.calculator = new window.teamStoreUtilities.PriceCalculator(
        this.metaobject,
        {
          priceLevel: this.teamProductData.priceLevel,
          isTeamAdmin: this.teamProductData.isTeamAdmin,
        }
      );
    }

    bindEvents() {
      if (!this.calculator) return;

      const price = this.calculator.getPrice(1);

      if (this.calculator.override) {
        this.updatePrice(price);
        return;
      }

      // If team admin, handle price tier UI + cart property
      if (this.teamProductData.isTeamAdmin && this.metaobject?.tier_pricing) {
        const priceTier =
          this.calculator.tierPricing?.[
            this.teamProductData.priceLevel?.toLowerCase().replace(/ /g, '_')
          ];

        if (priceTier && this.viewModel.$cartPropertiesPriceTierInput) {
          this.viewModel.$cartPropertiesPriceTierInput.value =
            JSON.stringify(priceTier);
        }

        this.setPriceToCartProperties(price);
        this.setPriceRange(priceTier);
        return;
      }

      this.updatePrice(price);
    }

    updatePrice(price) {
      this.hideInstallmentBlock();
      this.setNewPrice(this.formatPrice(price));
      this.setPriceToCartProperties(price);
    }

    /**
     * The installment widget is going to be wrong anyways because it's going to calculate based off the Shopify price,
     * which at time of this writing, was $9999.00, so hiding this avoids telling the customer they can get a t-shirt for the
     * low installment price of $902.49 a month.
     */
    hideInstallmentBlock() {
      const installmentForm = window.document.querySelector(
        '#product-form-installment'
      );
      if (installmentForm) {
        installmentForm.style.display = 'none';
      }
    }

    setPriceRange(priceTier) {
      if (!priceTier?.prices) return;

      const prices = Object.values(priceTier.prices);
      const minPrice = this.calculator._withUpcharge(Math.min(...prices));
      const maxPrice = this.calculator._withUpcharge(Math.max(...prices));

      if (minPrice === maxPrice) {
        this.setNewPrice(this.formatPrice(minPrice));
      } else {
        const formatted = `${this.formatPrice(minPrice)} - ${this.formatPrice(
          maxPrice
        )}`;
        this.setNewPrice(formatted);
      }
    }

    formatPrice(price) {
      return `$${Number.isInteger(+price) ? +price : +price.toFixed(2)}`;
    }

    setNewPrice(price) {
      this.viewModel.$priceText.innerHTML = price;
    }

    setPriceToCartProperties(price) {
      this.viewModel.$cartPropertiesPriceInput.value = price;
    }

    getViewModel(scope) {
      return {
        $embelishmentPrice: scope.querySelector('.embelishment-price'),
        $teamMoq: scope.querySelector('.team-moq'),
        $priceContainer: scope.querySelector(
          '.product__info-container .pricing'
        ),
        $cartPropertiesPriceInput: scope.querySelector('#ts_price'),
        $priceText: scope.querySelector('.price-item'),
        $cartPropertiesPriceTierInput: scope.querySelector('#ts_price_tier'),
      };
    }
  }

  window.shopifyClasses ??= {};
  window.shopifyClasses.TeamStorePriceChart = new TeamStorePriceChart();
} catch (e) {
  console.error('Error in TeamStorePriceChart class:', e);
}

try {
  class TeamProductPageDetails {
    constructor() {
      this.teamProductData = window.teamProductData;
      this.metaobject = window.teamProductData.metaobject;
      this.viewModel = this.getViewModel(
        document.querySelector('.product-container')
      );
      this.variables = {};

      this.init();
    }

    init() {
      if (!this.metaobject) {
        subscribeToTeamStoreMetaobject((metaobject, teamStoreCollection) => {
          this.teamProductData = window.teamProductData || this.teamProductData;
          this.metaobject = metaobject;
          this.teamProductData.metaobject = metaobject;
          if (teamStoreCollection) {
            this.teamProductData.teamStoreCollection = teamStoreCollection;
          }

          this.bindEvents();
        });
      } else {
        this.bindEvents();
      }
    }

    bindEvents() {
      this.setEmbelishmentPrice();
      this.setMoq();
      this.setCartItemProperties();
    }

    setCartItemProperties() {
      const isJSON = (str) => {
        try {
          JSON.parse(str);
          return true;
        } catch (e) {
          return false;
        }
      };

      for (const [key, value] of Object.entries(this.metaobject)) {
        const inputElement = this.viewModel.cartProperties[`$${key}`];
        if (inputElement) {
          inputElement.value = value;
        }
      }

      if (this.teamProductData?.priceLevel) {
        this.viewModel.cartProperties.$price_level.value =
          this.teamProductData.priceLevel;
      }

      const collectionProductsData =
        this.teamProductData.teamStoreCollection.team_product_data;
      if (isJSON(collectionProductsData)) {
        const imageUrl = JSON.parse(collectionProductsData)[
          this.metaobject.entry_sku
        ]?.imageLink;
        if (imageUrl) this.viewModel.cartProperties.$image.value = imageUrl;
      }
    }

    setEmbelishmentPrice() {
      const upcharge = this.metaobject.upcharge_price;

      if (upcharge && +upcharge !== 0) {
        this.viewModel.$embelishmentPrice.classList.remove(
          'visually-hidden',
          'visually-hidden--inline'
        );
        this.viewModel.$embelishmentPrice.innerText = `(includes $${upcharge} for embellishment)`;
      }
    }

    setMoq() {
      const moq = this.metaobject.min_order_qty;

      if (moq && moq !== 0 && this.teamProductData.isTeamAdmin) {
        this.viewModel.$teamMoq.classList.remove(
          'visually-hidden',
          'visually-hidden--inline'
        );
        this.viewModel.$teamMoq.innerText = `The minimum order quanity for this item is ${moq}`;
      }
    }

    getViewModel(scope) {
      return {
        $embelishmentPrice: scope.querySelector('.embelishment-price'),
        $teamMoq: scope.querySelector('.team-moq'),
        cartProperties: {
          $price: scope.querySelector('#ts_price'),
          $image: scope.querySelector('#ts_image'),
          $min_order_qty: scope.querySelector('#ts_min_order_qty'),
          $price_level: scope.querySelector('#ts_price_level'),
          $opportunity_id: scope.querySelector('#ts_opportunity_id'),
          $design_number: scope.querySelector('#ts_version_number'),
          $customization_embellishment_type: scope.querySelector(
            '#ts_customization_embellishment_type'
          ),
          $display_name: scope.querySelector('#ts_display_name'),
          $entry_sku: scope.querySelector('#ts_entry_sku'),
          $upcharge_price: scope.querySelector('#ts_upcharge'),
          $back_print: scope.querySelector('#ts_back_print'),
          $sleeve_print: scope.querySelector('#_ts_sleeve_print'),
          $personalization: scope.querySelector('#ts_personalization'),
        },
      };
    }
  }

  new TeamProductPageDetails();
} catch (e) {
  console.error('Error in TeamProductPageDetails class:', e);
}

try {
  class CustomValidator {
    constructor() { 
      this.teamProductData = window.teamProductData || {};
      this.viewModel = this.getViewModel(
        document.querySelector('.product-container')
      );
      this.metaobject = this.teamProductData.metaobject || null;
      this.init();
    }

    init() {
      if (!this.metaobject) {
        subscribeToTeamStoreMetaobject((metaobject, teamStoreCollection) => {
          this.teamProductData = window.teamProductData || this.teamProductData;
          this.metaobject = metaobject;
          this.teamProductData.metaobject = metaobject;
          if (teamStoreCollection) {
            this.teamProductData.teamStoreCollection = teamStoreCollection;
          }

          this.bindEvents();
        });
      } else {
        this.bindEvents();
      }
    }

    hasRequiredTeamStoreContext() {
      return window.teamStoreUtilities?.hasRequiredContext(this.teamProductData, this.metaobject);
    }

    bindEvents() {
      if (!this.hasRequiredTeamStoreContext()) return;

      if (this.metaobject?.personalization === 'true') {
        this.setLastNameFunctionality();
        this.addTocartValidations();
      }
    }

    setLastNameFunctionality() {
      const { $noLastNameCheckbox, $inputGroup, $nameInput } =
        this.viewModel.lastNameOption;

      if (!$noLastNameCheckbox || !$inputGroup || !$nameInput) return;

      $noLastNameCheckbox?.addEventListener('change', () => {
        $inputGroup.style.display = $noLastNameCheckbox.checked
          ? 'none'
          : 'block';
        $nameInput.value = '';
      });
      $nameInput?.addEventListener('input', () =>
        $nameInput.classList.remove('invalid')
      );
    }

    isCustomValidationPassed = () => {
      this.teamProductData = window.teamProductData || this.teamProductData;
      this.metaobject = this.metaobject || this.teamProductData.metaobject;

      if (!this.hasRequiredTeamStoreContext()) {
        return false;
      }

      const { $noLastNameCheckbox, $nameInput } = this.viewModel.lastNameOption;
      const personalizationEnabled = this.metaobject?.personalization === 'true';

      if (!personalizationEnabled) {
        return true;
      }

      if (!$nameInput || !$noLastNameCheckbox) {
        return false;
      }

      // Validate Last name input only if personalization true and checkbox "No name" is not checked
      if (
        !$nameInput.value &&
        !$noLastNameCheckbox.checked &&
        personalizationEnabled
      ) {
        $nameInput.classList.add('invalid');
        return false;
      }

      return true;
    };

    addTocartValidations() {
      const { $ctaButtons } = this.viewModel;

      $ctaButtons.forEach((ctaButton) => {
        ctaButton.addEventListener('click', (event) => {
          if (!this.isCustomValidationPassed()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        });
      });
    }

    getViewModel(scope) {
      return {
        $ctaButtons: scope.querySelectorAll('.product-form__submit'),

        lastNameOption: {
          $wrapper: scope.querySelector('.customization__last-name'),
          $noLastNameCheckbox: scope.querySelector(
            '.customization__last-name .customization__toggle #no-last-name'
          ),
          $inputGroup: scope.querySelector(
            '.customization__last-name .customization__input-group'
          ),
          $nameInput: scope.querySelector(
            '.customization__last-name #Custom-Product-Text'
          ),
        },
      };
    }
  }

  window.shopifyClasses ??= {};
  window.shopifyClasses.CustomValidator = new CustomValidator();
} catch (e) {
  console.error('Error in CustomValidator class:', e);
}
